import React, { useState, useEffect, useRef } from "react";
import { Form, Input, Button, Checkbox, message } from "antd";
import {
	UserOutlined,
	LockOutlined,
	GoogleOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { AuthLayout } from "@/shared/layout";
import { CodeInput, VerificationButton } from "@/features/auth";
import { authenticationService, LoginRequest, OTPLoginRequest } from "@/api/authentication";
import { useAuth } from "@/shared/contexts/AuthContext";
import { normalizeEmail } from "@/shared/utils/email";

// Styled Components for form-specific elements
const SuccessMessage = styled.div`
	background: #f6ffed;
	border: 1px solid #b7eb8f;
	border-radius: 8px;
	padding: 0.75rem 1rem;
	margin-bottom: 1.5rem;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	color: #52c41a;
	font-size: 0.875rem;
`;

const ErrorMessage = styled.div`
	background: #fff2f0;
	border: 1px solid #ffccc7;
	border-radius: 8px;
	padding: 0.75rem 1rem;
	margin-bottom: 0rem;
	display: flex;
	align-items: flex-start;
	gap: 0.5rem;
	color: #ff4d4f;
	font-size: 0.875rem;

	.error-icon {
		color: #ff4d4f;
		font-size: 1rem;
		margin-top: 0.125rem;
	}

	.error-content {
		flex: 1;
	}

	.error-title {
		font-weight: 700;
		color: #000;
		margin-bottom: 0.25rem;
	}

	.error-message {
		color: #000;
		font-size: 0.875rem;
	}
`;

const StyledForm = styled(Form)`
	min-height: 300px;
	height: auto;
	padding-top: 48px;

	.ant-form-item {
		margin-bottom: 1.25rem;
	}

	.ant-form-item-label {
		padding-bottom: 0.5rem;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #333;
	}

	.ant-input-affix-wrapper {
		height: 36px;
		border-radius: 20px;
		border: 1px solid #d9d9d9;

		&:hover,
		&:focus {
			border-color: ${CUSTOM_COLORS.PRIMARY};
		}
	}

	.ant-input {
		font-size: 0.9rem;
	}
`;

const ForgotPasswordLink = styled(Link)`
	color: #666;
	font-size: 0.875rem;
	text-decoration: none;

	&:hover {
		color: ${CUSTOM_COLORS.PRIMARY};
	}
`;

const LoginButton = styled(Button)`
	height: 36px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	background: ${CUSTOM_COLORS.PRIMARY};
	border: none;
	width: 100%;

	&:hover {
		background: ${CUSTOM_COLORS.PRIMARY} !important;
		opacity: 0.9;
	}
`;

const GoogleButton = styled(Button)`
	height: 36px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	border: 1px solid #d9d9d9;
	background: white;
	color: #333;
	width: 100%;
	margin-top: 16px;

	&:hover {
		border-color: ${CUSTOM_COLORS.PRIMARY};
		color: ${CUSTOM_COLORS.PRIMARY};
	}
`;

const OTCButton = styled(Button)`
	color: #ffffff;
	height: 36px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	background: ${CUSTOM_COLORS.SECONDARY};
	border: none;
	width: 100%;

	&:hover {
		background: ${CUSTOM_COLORS.SECONDARY} !important;
		opacity: 0.9;
	}
`;

const DividerText = styled.div`
	text-align: center;
	margin: 1.25rem 0;
	color: #999;
	font-size: 0.8rem;
	position: relative;

	&::before {
		content: "";
		position: absolute;
		top: 50%;
		left: 0;
		right: 0;
		height: 1px;
		background: #e8e8e8;
		z-index: 1;
	}

	span {
		background: white;
		padding: 0 1rem;
		position: relative;
		z-index: 2;
	}
`;

const SendCodeLink = styled.button`
	background: none;
	border: none;
	color: ${CUSTOM_COLORS.PRIMARY};
	font-size: 0.875rem;
	font-weight: 500;
	cursor: pointer;
	text-decoration: none;
	margin-top: 0.5rem;
	margin-left: auto;
	display: block;

	&:hover {
		color: ${CUSTOM_COLORS.PRIMARY};
		opacity: 0.8;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const OrganizationLogin: React.FC = () => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [loginMode, setLoginMode] = useState<"regular" | "otp">("regular");
	const [showCodeVerification, setShowCodeVerification] = useState(false);
	const [verificationCode, setVerificationCode] = useState("");
	const [canSendCode, setCanSendCode] = useState(true);
	const [countdown, setCountdown] = useState(0);
	const [userEmail, setUserEmail] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [resendCount, setResendCount] = useState(0);
	const lastAutoSubmittedCodeRef = useRef<string | null>(null);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const returnTo = searchParams.get("returnTo") || "/campaigns";
	const { login } = useAuth();

	useEffect(() => {
		if (countdown > 0) {
			const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
			return () => clearTimeout(timer);
		} else {
			setCanSendCode(true);
		}
	}, [countdown]);

	useEffect(() => {
		if (loginMode !== "otp" || !showCodeVerification || loading) {
			return;
		}

		const normalizedCode = verificationCode.replace(/\D/g, "").slice(0, 6);

		if (normalizedCode.length === 6 && lastAutoSubmittedCodeRef.current !== normalizedCode) {
			lastAutoSubmittedCodeRef.current = normalizedCode;
			form.submit();
		}
	}, [verificationCode, loginMode, showCodeVerification, loading, form]);

	const handleSendCode = async () => {
		const email = form.getFieldValue("email");
		if (!email) {
			setErrorMessage("Please enter your email first");
			return;
		}

		setLoading(true);
		setErrorMessage(""); // Clear any previous errors
		try {
			// Call API to generate OTP for organization
			await authenticationService.generateOrganizationLoginOTP({ email });
			setUserEmail(email);
			setVerificationCode("");
			lastAutoSubmittedCodeRef.current = null;
			message.success("Verification code sent to your email!");
			setCanSendCode(false);
			setCountdown(60); // 60 seconds cooldown
			setShowCodeVerification(true);
			setResendCount((prev) => prev + 1);
		} catch (error: any) {
			const errorMsg = error.response?.data?.message || "Internal Server Error";
			setErrorMessage(errorMsg);
			setVerificationCode("");
			setResendCount((prev) => prev + 1);
		} finally {
			setLoading(false);
		}
	};

	const handleCodeComplete = (code: string) => {
		setVerificationCode(code.replace(/\D/g, "").slice(0, 6));
	};

	const handleCodeChange = (code: string) => {
		const normalizedCode = code.replace(/\D/g, "").slice(0, 6);
		setVerificationCode(normalizedCode);
		if (normalizedCode.length < 6) {
			lastAutoSubmittedCodeRef.current = null;
		}
	};

	const onFinish = async (values: any) => {
		setErrorMessage(""); // Clear any previous errors

		if (loginMode === "regular") {
			// Admin login flow: email + password
			setLoading(true);
			try {
				const loginData: LoginRequest = {
					email: normalizeEmail(values.email),
					password: values.password,
				};

				const response = await authenticationService.organizationLogin(loginData);

				// Check if password reset is required (first-time login)
				if (response.reset_password_required) {
					// Store the access token for reset password API calls
					authenticationService.setAccessToken(response.accessToken);
					// Store email in localStorage for reset password
					localStorage.setItem("organization_email", normalizeEmail(values.email));
					message.info("Please set your password to continue");
					navigate("/amplify/reset-password");
				} else {
					// Update authentication context for normal login
					login(response.user);
					message.success(`Welcome back, ${response.user.name}!`);
					navigate(returnTo);
				}
			} catch (error: any) {
				const errorMsg = error.response?.data?.message || "Internal Server Error";
				setErrorMessage(errorMsg);
			} finally {
				setLoading(false);
			}
		} else if (loginMode === "otp") {
			const normalizedCode = verificationCode.replace(/\D/g, "").slice(0, 6);
			if (normalizedCode.length !== 6) {
				setErrorMessage("Please enter a valid 6-digit code");
				return;
			}

			setLoading(true);
			try {
				const otpEmail = normalizeEmail(userEmail || values.email);
				const otpLoginData: OTPLoginRequest = {
					email: otpEmail,
					otp: normalizedCode,
				};

				const response = await authenticationService.organizationLoginWithOTP(otpLoginData);

				// Check if password reset is required (first-time login)
				if (response.reset_password_required) {
					// Store the access token for reset password API calls
					authenticationService.setAccessToken(response.accessToken);
					// Store email in localStorage for reset password
					localStorage.setItem("organization_email", otpEmail);
					message.info("Please set your password to continue");
					navigate("/amplify/reset-password");
				} else {
					// Update authentication context for normal login
					login(response.user);
					message.success(`Welcome back, ${response.user.name}!`);
					navigate(returnTo);
				}
			} catch (error: any) {
				const errorMsg = error.response?.data?.message || "Internal Server Error";
				setErrorMessage(errorMsg);
				setVerificationCode("");
				setResendCount((prev) => prev + 1);
				lastAutoSubmittedCodeRef.current = null;
			} finally {
				setLoading(false);
			}
		}
	};

	const handleGoogleLogin = () => {
		message.info("Google login functionality coming soon!");
	};

	const handleOTPLogin = () => {
		setLoginMode("otp");
		setShowCodeVerification(false);
		setVerificationCode("");
		lastAutoSubmittedCodeRef.current = null;
		setErrorMessage(""); // Clear any previous errors
		form.resetFields();
	};

	const handleRegularLogin = () => {
		setLoginMode("regular");
		setShowCodeVerification(false);
		setVerificationCode("");
		lastAutoSubmittedCodeRef.current = null;
		setErrorMessage(""); // Clear any previous errors
		form.resetFields();
	};

	return (
		<AuthLayout
			title={showCodeVerification ? "Get a 6-digit code" : "Welcome!"}
			subtitle={
				showCodeVerification ? `We sent a temporary sign in code to ${userEmail}` : "Log in to the Creatorverse"
			}
			isOrganization={true}
			backLinkTo={searchParams.get("returnTo") || undefined}
		>
			<SuccessMessage style={{ visibility: "hidden" }}>
				<CheckCircleOutlined />
				<span>Successful</span>
				<span>•</span>
				<span>You are now logging in</span>
			</SuccessMessage>

			{errorMessage && (
				<ErrorMessage>
					<CloseCircleOutlined className="error-icon" />
					<div className="error-content">
						<div className="error-title">Error</div>
						<div className="error-message">{errorMessage}</div>
					</div>
				</ErrorMessage>
			)}

			<StyledForm
				form={form}
				name="login"
				onFinish={onFinish}
				layout="vertical"
				initialValues={{
					remember: true,
				}}
			>
				<Form.Item
					name="email"
					label="Email"
					rules={[
						{ message: "Please input your email!" },
						{ type: "email", message: "Please enter a valid email!" },
					]}
				>
					<Input
						prefix={<UserOutlined />}
						placeholder="Enter here"
						size="large"
						disabled={showCodeVerification}
					/>
				</Form.Item>

				{loginMode === "regular" && (
					<Form.Item name="password" label="Password" rules={[{ message: "Please input your password!" }]}>
						<Input.Password prefix={<LockOutlined />} placeholder="Password" size="large" />
					</Form.Item>
				)}

				{loginMode === "otp" && (
					<Form.Item label="Code" style={{ marginBottom: 0 }}>
						<CodeInput
							resetSignal={resendCount}
							onComplete={handleCodeComplete}
							onChange={handleCodeChange}
						/>
						<div style={{ display: "flex", justifyContent: "flex-end" }}>
							<SendCodeLink onClick={handleSendCode} disabled={!canSendCode || loading}>
								{canSendCode ? "SEND CODE" : `Resend in ${countdown}s`}
							</SendCodeLink>
						</div>
					</Form.Item>
				)}

				{loginMode === "regular" && (
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: "1.5rem",
						}}
					>
						<Form.Item name="remember" valuePropName="checked" noStyle>
							<Checkbox>Remember me</Checkbox>
						</Form.Item>
						<ForgotPasswordLink to="/forgot-password">Forgot password?</ForgotPasswordLink>
					</div>
				)}

				<Form.Item>
					{loginMode === "regular" ? (
						<LoginButton type="primary" htmlType="submit" loading={loading}>
							Log in
						</LoginButton>
					) : (
						<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
							{showCodeVerification && (
								<VerificationButton
									type="submit"
									idleVariant="primary"
									verifying={loading}
									disabled={verificationCode.length !== 6 || loading}
									style={{ order: 1 }}
								>
									Continue
								</VerificationButton>
							)}
							<OTCButton htmlType="button" onClick={handleRegularLogin} style={{ order: 2 }}>
								Back
							</OTCButton>
						</div>
					)}
				</Form.Item>
			</StyledForm>

			{loginMode === "regular" && (
				<>
					<GoogleButton icon={<GoogleOutlined />} onClick={handleGoogleLogin}>
						Log in with Google
					</GoogleButton>

					<DividerText>
						<span>OR</span>
					</DividerText>

					{loginMode === "regular" ? (
						<OTCButton onClick={handleOTPLogin}>Continue with OTP</OTCButton>
					) : (
						<OTCButton icon={<UserOutlined />} onClick={handleRegularLogin}>
							Continue with Password
						</OTCButton>
					)}
				</>
			)}
		</AuthLayout>
	);
};

export default OrganizationLogin;
